import { useRef, useCallback } from 'react';
import { LIMITS } from '@roll1/shared';
import type { FogReveal, Camera } from '../types';
import { worldToScreen, getViewportBounds, isInViewport } from '../utils';
import type { CanvasEngine } from './useCanvasEngine';

/** Offscreen canvas cache for the fog layer */
interface OffscreenLayerCache {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  valid: boolean;
  width: number;
  height: number;
  /** Camera state when cache was last built — used for transform-based fast redraws */
  cam: Camera;
}

export type FogDrawMode = 'brush' | 'rect' | 'polygon';

export interface UseFogReturn {
  enabled: React.MutableRefObject<boolean>;
  revealed: React.MutableRefObject<FogReveal[]>;
  closings: React.MutableRefObject<FogReveal[]>;
  reFogMode: React.MutableRefObject<boolean>;
  brushSize: React.MutableRefObject<number>;
  setBrushSize: (size: number) => void;
  fogMode: React.MutableRefObject<FogDrawMode>;
  /** Current fog rendering mode: manual (GM-drawn) or dynamic (visibility-based) */
  fogModeRef: React.MutableRefObject<'manual' | 'dynamic'>;
  rectStart: React.MutableRefObject<{ x: number; y: number } | null>;
  rectEnd: React.MutableRefObject<{ x: number; y: number } | null>;
  polygonPoints: React.MutableRefObject<Array<{ x: number; y: number }>>;
  toggle: () => boolean;
  toggleReFogMode: () => boolean;
  startReveal: (wx: number, wy: number) => void;
  continueReveal: (wx: number, wy: number) => void;
  endReveal: () => void;
  startClose: (wx: number, wy: number) => void;
  continueClose: (wx: number, wy: number) => void;
  endClose: () => void;
  startRectReveal: (wx: number, wy: number) => void;
  updateRectPreview: (wx: number, wy: number) => void;
  endRectReveal: (wx: number, wy: number) => void;
  addPolygonPoint: (wx: number, wy: number) => void;
  finishPolygon: () => void;
  cancelPolygon: () => void;
  coverAll: () => void;
  clearAll: () => void;
  resetFog: () => void;
  renderFog: () => void;
  /** Set a dynamic fog render callback. When fogModeRef='dynamic', renderFog delegates to this. */
  setDynamicFogRenderer: (renderer: (() => void) | null) => void;
  /** Invalidate the offscreen fog cache (call on camera change / resize / page switch) */
  invalidateCache: () => void;
}

export function useFog(engine: CanvasEngine, isGM = true): UseFogReturn {
  const enabled = useRef(false);
  const revealed = useRef<FogReveal[]>([]);
  const closings = useRef<FogReveal[]>([]);
  const reFogMode = useRef(false);
  const isRevealing = useRef(false);
  const isClosing = useRef(false);
  const isGMRef = useRef(isGM);
  isGMRef.current = isGM;
  const loadBrushSize = () => {
    try {
      const stored = localStorage.getItem('fog-brush-size');
      return stored ? Math.max(20, Math.min(300, Number(stored))) : 70;
    } catch { return 70; }
  };
  const brushSize = useRef(loadBrushSize());
  const setBrushSize = useCallback((size: number) => {
    const clamped = Math.max(20, Math.min(300, size));
    brushSize.current = clamped;
    try { localStorage.setItem('fog-brush-size', String(clamped)); } catch {}
  }, []);
  const fogModeRef = useRef<'manual' | 'dynamic'>('manual');
  const dynamicFogRendererRef = useRef<(() => void) | null>(null);

  const setDynamicFogRenderer = useCallback((renderer: (() => void) | null) => {
    dynamicFogRendererRef.current = renderer;
  }, []);

  const lastRevealPos = useRef<{ x: number; y: number } | null>(null);
  const lastClosePos = useRef<{ x: number; y: number } | null>(null);

  const hasReachedRevealLimit = useCallback(() => {
    return revealed.current.length >= LIMITS.MAX_FOG_REVEALS_PER_PAGE;
  }, []);

  // ── Offscreen fog cache ──
  const fogCacheRef = useRef<OffscreenLayerCache | null>(null);

  function ensureFogCache(w: number, h: number): OffscreenLayerCache {
    let cache = fogCacheRef.current;
    if (!cache) {
      const canvas = document.createElement('canvas');
      cache = { canvas, ctx: canvas.getContext('2d')!, valid: false, width: 0, height: 0, cam: { zoom: 1, offsetX: 0, offsetY: 0 } };
      fogCacheRef.current = cache;
    }
    if (cache.width !== w || cache.height !== h) {
      cache.canvas.width = Math.round(w);
      cache.canvas.height = Math.round(h);
      cache.width = w;
      cache.height = h;
      cache.valid = false;
    }
    return cache;
  }

  const invalidateCache = useCallback(() => {
    if (fogCacheRef.current) {
      fogCacheRef.current.valid = false;
    }
  }, []);

  const toggle = useCallback((): boolean => {
    enabled.current = !enabled.current;
    invalidateCache();
    engine.markDirty('fog');
    return enabled.current;
  }, [engine, invalidateCache]);

  const startReveal = useCallback((wx: number, wy: number) => {
    if (!enabled.current) {
      enabled.current = true;
    }
    if (hasReachedRevealLimit()) {
      isRevealing.current = false;
      return;
    }
    isRevealing.current = true;
    lastRevealPos.current = { x: wx, y: wy };
    revealed.current.push({ type: 'circle', x: wx, y: wy, radius: brushSize.current });
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, hasReachedRevealLimit, invalidateCache]);

  const continueReveal = useCallback((wx: number, wy: number) => {
    if (!isRevealing.current) return;
    if (hasReachedRevealLimit()) {
      isRevealing.current = false;
      return;
    }
    // Skip if mouse hasn't moved far enough from last reveal point
    const last = lastRevealPos.current;
    if (last) {
      const dx = wx - last.x;
      const dy = wy - last.y;
      const minDist = brushSize.current * 0.5;
      if (dx * dx + dy * dy < minDist * minDist) return;
    }
    lastRevealPos.current = { x: wx, y: wy };
    revealed.current.push({ type: 'circle', x: wx, y: wy, radius: brushSize.current });
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, hasReachedRevealLimit, invalidateCache]);

  const endReveal = useCallback(() => {
    isRevealing.current = false;
    lastRevealPos.current = null;
  }, []);

  const hasReachedClosingLimit = useCallback(() => {
    return closings.current.length >= LIMITS.MAX_FOG_CLOSINGS_PER_PAGE;
  }, []);

  const toggleReFogMode = useCallback((): boolean => {
    reFogMode.current = !reFogMode.current;
    return reFogMode.current;
  }, []);

  const startClose = useCallback((wx: number, wy: number) => {
    if (!enabled.current) {
      enabled.current = true;
    }
    if (hasReachedClosingLimit()) {
      isClosing.current = false;
      return;
    }
    isClosing.current = true;
    lastClosePos.current = { x: wx, y: wy };
    closings.current.push({ type: 'circle', x: wx, y: wy, radius: brushSize.current });
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, hasReachedClosingLimit, invalidateCache]);

  const continueClose = useCallback((wx: number, wy: number) => {
    if (!isClosing.current) return;
    if (hasReachedClosingLimit()) {
      isClosing.current = false;
      return;
    }
    const last = lastClosePos.current;
    if (last) {
      const dx = wx - last.x;
      const dy = wy - last.y;
      const minDist = brushSize.current * 0.5;
      if (dx * dx + dy * dy < minDist * minDist) return;
    }
    lastClosePos.current = { x: wx, y: wy };
    closings.current.push({ type: 'circle', x: wx, y: wy, radius: brushSize.current });
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, hasReachedClosingLimit, invalidateCache]);

  const endClose = useCallback(() => {
    isClosing.current = false;
    lastClosePos.current = null;
  }, []);

  const coverAll = useCallback(() => {
    revealed.current = [];
    closings.current = [];
    enabled.current = true;
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, invalidateCache]);

  const clearAll = useCallback(() => {
    revealed.current = [];
    closings.current = [];
    enabled.current = false;
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, invalidateCache]);

  const resetFog = useCallback(() => {
    revealed.current = [];
    closings.current = [];
    invalidateCache();
    engine.markDirty('fog');
  }, [engine, invalidateCache]);

  /** Check if a FogReveal is within the viewport bounds (AABB overlap) */
  function isRevealInViewport(r: FogReveal, vp: { minX: number; minY: number; maxX: number; maxY: number }): boolean {
    const revealType = (r as Record<string, unknown>).type as string | undefined ?? 'circle';
    switch (revealType) {
      case 'circle': {
        const c = r as Extract<FogReveal, { type: 'circle' }>;
        return isInViewport(c.x, c.y, c.radius, vp);
      }
      case 'rect': {
        const rc = r as Extract<FogReveal, { type: 'rect' }>;
        return rc.x + rc.width > vp.minX && rc.x < vp.maxX && rc.y + rc.height > vp.minY && rc.y < vp.maxY;
      }
      case 'polygon': {
        const pg = r as Extract<FogReveal, { type: 'polygon' }>;
        if (pg.points.length < 3) return false;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const p of pg.points) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
        return maxX > vp.minX && minX < vp.maxX && maxY > vp.minY && minY < vp.maxY;
      }
      default:
        return false;
    }
  }

  /** Render a single fog reveal cutout (with gradient for circles in cached mode) */
  function renderRevealCutout(c: CanvasRenderingContext2D, r: FogReveal, cam: Camera, withGradient: boolean): void {
    const revealType = (r as Record<string, unknown>).type as string | undefined ?? 'circle';
    switch (revealType) {
      case 'circle': {
        const cr = r as Extract<FogReveal, { type: 'circle' }>;
        const sp = worldToScreen(cr.x, cr.y, cam);
        const sr = cr.radius * cam.zoom;
        if (withGradient) {
          const gradient = c.createRadialGradient(sp.x, sp.y, 0, sp.x, sp.y, sr);
          gradient.addColorStop(0, 'rgba(0,0,0,1)');
          gradient.addColorStop(0.7, 'rgba(0,0,0,0.8)');
          gradient.addColorStop(1, 'rgba(0,0,0,0)');
          c.fillStyle = gradient;
        }
        c.beginPath();
        c.arc(sp.x, sp.y, sr, 0, Math.PI * 2);
        c.fill();
        break;
      }
      case 'rect': {
        const rc = r as Extract<FogReveal, { type: 'rect' }>;
        const sp = worldToScreen(rc.x, rc.y, cam);
        const sw = rc.width * cam.zoom;
        const sh = rc.height * cam.zoom;
        c.fillStyle = 'rgba(0,0,0,1)';
        c.fillRect(sp.x, sp.y, sw, sh);
        break;
      }
      case 'polygon': {
        const pg = r as Extract<FogReveal, { type: 'polygon' }>;
        if (pg.points.length < 3) break;
        c.fillStyle = 'rgba(0,0,0,1)';
        c.beginPath();
        const first = worldToScreen(pg.points[0].x, pg.points[0].y, cam);
        c.moveTo(first.x, first.y);
        for (let i = 1; i < pg.points.length; i++) {
          const p = worldToScreen(pg.points[i].x, pg.points[i].y, cam);
          c.lineTo(p.x, p.y);
        }
        c.closePath();
        c.fill();
        break;
      }
    }
  }

  /** Render a single fog closing (re-darken) fill in the fog color */
  function renderClosingFill(c: CanvasRenderingContext2D, r: FogReveal, cam: Camera, color: string): void {
    const revealType = (r as Record<string, unknown>).type as string | undefined ?? 'circle';
    switch (revealType) {
      case 'circle': {
        const cr = r as Extract<FogReveal, { type: 'circle' }>;
        const sp = worldToScreen(cr.x, cr.y, cam);
        const sr = cr.radius * cam.zoom;
        c.fillStyle = color;
        c.beginPath();
        c.arc(sp.x, sp.y, sr, 0, Math.PI * 2);
        c.fill();
        break;
      }
      case 'rect': {
        const rc = r as Extract<FogReveal, { type: 'rect' }>;
        const sp = worldToScreen(rc.x, rc.y, cam);
        const sw = rc.width * cam.zoom;
        const sh = rc.height * cam.zoom;
        c.fillStyle = color;
        c.fillRect(sp.x, sp.y, sw, sh);
        break;
      }
      case 'polygon': {
        const pg = r as Extract<FogReveal, { type: 'polygon' }>;
        if (pg.points.length < 3) break;
        c.fillStyle = color;
        c.beginPath();
        const first = worldToScreen(pg.points[0].x, pg.points[0].y, cam);
        c.moveTo(first.x, first.y);
        for (let i = 1; i < pg.points.length; i++) {
          const p = worldToScreen(pg.points[i].x, pg.points[i].y, cam);
          c.lineTo(p.x, p.y);
        }
        c.closePath();
        c.fill();
        break;
      }
    }
  }

  const renderFog = useCallback(() => {
    const canvas = engine.refs.current.fog;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.width / dpr;
    const cssH = canvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cam = engine.cameraRef.current;
    ctx.clearRect(0, 0, cssW, cssH);
    if (!enabled.current) {
      return;
    }

    // BI-001: fogModeRef stays in sync with activePage.fog.mode
    // Dynamic mode: delegate to dynamic fog renderer
    if (fogModeRef.current === 'dynamic') {
      if (dynamicFogRendererRef.current) {
        dynamicFogRendererRef.current();
      } else {
        // Renderer not wired yet (effect hasn't fired) — re-request next frame
        engine.markDirty('fog');
      }
      return;
    }

    const cache = ensureFogCache(canvas.width, canvas.height);

    const fogColor = isGMRef.current ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 1)';

    if (!cache.valid) {
      // Rebuild offscreen cache at current camera (at backing-store resolution)
      const oc = cache.ctx;
      oc.setTransform(dpr, 0, 0, dpr, 0, 0);
      oc.clearRect(0, 0, cssW, cssH);
      oc.fillStyle = fogColor;
      oc.fillRect(0, 0, cssW, cssH);

      oc.globalCompositeOperation = 'destination-out';
      const vp = getViewportBounds(cam, cssW, cssH, 200);
      for (const r of revealed.current) {
        if (!isRevealInViewport(r, vp)) continue;
        renderRevealCutout(oc, r, cam, true);
      }
      // Paint closings: re-darken previously revealed areas
      oc.globalCompositeOperation = 'source-over';
      for (const c of closings.current) {
        if (!isRevealInViewport(c, vp)) continue;
        renderClosingFill(oc, c, cam, fogColor);
      }
      cache.cam = { ...cam };
      cache.valid = true;
    }

    // Draw from cache — if camera hasn't changed, direct blit; otherwise fast redraw
    const cc = cache.cam;
    if (cc.zoom === cam.zoom && cc.offsetX === cam.offsetX && cc.offsetY === cam.offsetY) {
      // Camera unchanged — pixel-perfect blit (bypass DPR transform)
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(cache.canvas, 0, 0);
      ctx.restore();
    } else {
      // Camera changed (zoom/pan in progress) — fast redraw without gradients.
      ctx.fillStyle = fogColor;
      ctx.fillRect(0, 0, cssW, cssH);

      ctx.globalCompositeOperation = 'destination-out';
      const vp = getViewportBounds(cam, cssW, cssH, 200);
      for (const r of revealed.current) {
        if (!isRevealInViewport(r, vp)) continue;
        renderRevealCutout(ctx, r, cam, false);
      }
      // Paint closings: re-darken previously revealed areas
      ctx.globalCompositeOperation = 'source-over';
      for (const c of closings.current) {
        if (!isRevealInViewport(c, vp)) continue;
        renderClosingFill(ctx, c, cam, fogColor);
      }
    }
  }, [engine]);

  // ── Fog draw mode (brush/rect/polygon) ──
  const fogMode = useRef<FogDrawMode>('brush');
  const rectStart = useRef<{ x: number; y: number } | null>(null);
  const rectEnd = useRef<{ x: number; y: number } | null>(null);
  const polygonPoints = useRef<Array<{ x: number; y: number }>>([]);

  const startRectReveal = useCallback((wx: number, wy: number) => {
    rectStart.current = { x: wx, y: wy };
    rectEnd.current = { x: wx, y: wy };
  }, []);

  const updateRectPreview = useCallback((wx: number, wy: number) => {
    rectEnd.current = { x: wx, y: wy };
    engine.markDirty('ui');
  }, [engine]);

  const endRectReveal = useCallback((wx: number, wy: number) => {
    const start = rectStart.current;
    if (!start) return;
    const x = Math.min(start.x, wx);
    const y = Math.min(start.y, wy);
    const width = Math.abs(wx - start.x);
    const height = Math.abs(wy - start.y);
    if (width < 5 || height < 5) { rectStart.current = null; rectEnd.current = null; return; }

    if (!enabled.current) enabled.current = true;
    const reveal: FogReveal = { type: 'rect', x, y, width, height };
    if (reFogMode.current) {
      closings.current.push(reveal);
    } else {
      revealed.current.push(reveal);
    }
    rectStart.current = null;
    rectEnd.current = null;
    invalidateCache();
    engine.markDirty('fog');
    engine.markDirty('ui');
  }, [engine, invalidateCache]);

  const addPolygonPoint = useCallback((wx: number, wy: number) => {
    polygonPoints.current.push({ x: wx, y: wy });
    engine.markDirty('ui');
  }, [engine]);

  const finishPolygon = useCallback(() => {
    const pts = polygonPoints.current;
    if (pts.length < 3) { polygonPoints.current = []; engine.markDirty('ui'); return; }

    if (!enabled.current) enabled.current = true;
    const reveal: FogReveal = { type: 'polygon', points: [...pts] };
    if (reFogMode.current) {
      closings.current.push(reveal);
    } else {
      revealed.current.push(reveal);
    }
    polygonPoints.current = [];
    invalidateCache();
    engine.markDirty('fog');
    engine.markDirty('ui');
  }, [engine, invalidateCache]);

  const cancelPolygon = useCallback(() => {
    polygonPoints.current = [];
    engine.markDirty('ui');
  }, [engine]);

  return {
    enabled, revealed, closings, reFogMode,
    brushSize, setBrushSize,
    fogMode, fogModeRef, rectStart, rectEnd, polygonPoints,
    toggle, toggleReFogMode,
    startReveal, continueReveal, endReveal,
    startClose, continueClose, endClose,
    startRectReveal, updateRectPreview, endRectReveal,
    addPolygonPoint, finishPolygon, cancelPolygon,
    coverAll, clearAll,
    resetFog, renderFog, setDynamicFogRenderer, invalidateCache,
  };
}
